import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, MessageCircle } from "lucide-react";
import { api, mediaUrl } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { ShareButton } from "../components/ShareButton";
import { DogBirthday } from "../components/DogBirthday";
import { BirthdayWishes } from "../components/BirthdayWishes";

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" });

const DogDiary = () => {
  const { dogName } = useParams();
  const [diary, setDiary] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/dogs/${encodeURIComponent(dogName)}`)
      .then(({ data }) => setDiary(data))
      .catch(() => setNotFound(true));
  }, [dogName]);

  if (notFound)
    return (
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <p data-testid="diary-not-found" className="font-display text-3xl italic text-[#5C534B]">
          Nessun diario trovato per questo bassotto.
        </p>
        <Link to="/cani" className="mt-6 inline-block text-sm font-semibold text-[#B85328] underline underline-offset-4">
          Vedi tutti i bassotti
        </Link>
      </main>
    );
  if (!diary)
    return (
      <main className="flex min-h-[60vh] items-center justify-center">
        <p className="font-display text-2xl italic text-[#8C7B6E]">Caricamento...</p>
      </main>
    );

  return (
    <main className="mx-auto max-w-3xl px-4 pb-24 sm:px-8">
      <button
        data-testid="diary-back-button"
        onClick={() => navigate(-1)}
        className="mt-8 flex items-center gap-2 text-sm font-semibold text-[#8C7B6E] transition-colors hover:text-[#B85328]"
      >
        <ArrowLeft size={15} /> Indietro
      </button>

      <section className="py-10 sm:py-14">
        <Reveal>
          <p className="eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-8 bg-[#B85328]" />
            Il diario di crescita
          </p>
          <h1
            data-testid="diary-dog-heading"
            className="font-display mt-4 text-4xl font-bold leading-[1.08] tracking-tight text-[#2C2723] sm:text-5xl lg:text-6xl"
          >
            {diary.dog_name}
          </h1>
          <p data-testid="diary-stats" className="mt-4 text-sm leading-relaxed text-[#8C7B6E]">
            {diary.memories.length} {diary.memories.length === 1 ? "ricordo" : "ricordi"} condivisi da{" "}
            {diary.authors.join(", ")} — dal {formatDate(diary.memories[0].created_at)}
          </p>
          <DogBirthday
            dogName={diary.dog_name}
            birthDate={diary.birth_date}
            canEdit={diary.can_edit_birthday}
            onSaved={(birth_date) => setDiary((d) => ({ ...d, birth_date }))}
          />
          <ShareButton
            path={`/cane/${encodeURIComponent(diary.dog_name)}`}
            title={`Il diario di ${diary.dog_name} — Le Nuove Vite`}
            text={`Tutti i ricordi di ${diary.dog_name}, un bassotto della Casetta`}
            testId="diary-share-button"
            className="mt-6"
          />
        </Reveal>
      </section>

      <BirthdayWishes dogName={diary.dog_name} birthDate={diary.birth_date} />

      <div data-testid="diary-timeline" className="space-y-0">
        {diary.memories.map((post, i) => (
          <Reveal key={post.post_id} delay={0.05}>
            <div
              data-testid={`diary-chapter-${post.post_id}`}
              className="group grid gap-6 border-t border-[#CFC0B2] py-10 first:border-t-0 sm:grid-cols-[auto_1fr]"
            >
              <div className="flex sm:flex-col sm:items-center sm:gap-2">
                <span className="font-display text-5xl font-semibold text-[#CFC0B2] transition-colors duration-500 group-hover:text-[#B85328]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="ml-3 self-center font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C7B6E] sm:ml-0 sm:text-center">
                  {formatDate(post.created_at)}
                </span>
              </div>
              <Link
                to={`/ricordo/${post.post_id}`}
                data-testid={`diary-post-link-${post.post_id}`}
                className="block overflow-hidden rounded-3xl border border-[#E8DFD5] bg-white transition-all duration-300 hover:-translate-y-1 hover:border-[#B85328]/40 hover:shadow-xl"
              >
                {post.media_type === "video" ? (
                  <video src={mediaUrl(post.media_url)} controls preload="metadata" className="max-h-96 w-full object-cover" />
                ) : (
                  <img src={mediaUrl(post.media_url)} alt={`${diary.dog_name} — capitolo ${i + 1}`} loading="lazy" className="max-h-96 w-full object-cover" />
                )}
                <div className="p-5">
                  {post.caption && <p className="text-sm leading-relaxed text-[#5C534B]">{post.caption}</p>}
                  <div className="mt-3 flex items-center gap-4 text-xs font-semibold text-[#8C7B6E]">
                    <span className="flex items-center gap-1.5">
                      <Heart size={13} /> {post.likes_count}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <MessageCircle size={13} /> {post.comments_count}
                    </span>
                    <span className="ml-auto">di {post.author_name}</span>
                  </div>
                </div>
              </Link>
            </div>
          </Reveal>
        ))}
      </div>
    </main>
  );
};

export default DogDiary;
